import React, { useState } from 'react'
import Papa from 'papaparse'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { stockDeliveryBXService } from 'BoostXpressAdmin/Services/stockDeliveryBXService'
import {
  StockDeliveryItem,
  StockDeliveryProduct,
  StockDeliveryStatus,
} from 'BoostXpressAdmin/Model/stockDeliveryXModel'
import { useDebouncedValue, useStationOperationsPage } from '../hooks/useStationOperationsList'
import {
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'
import './StockDelivery.scss'

const StockDelivery: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const deferredStationId = useDebouncedValue(searchTerm.trim())
  const [statusFilter, setStatusFilter] = useState<StockDeliveryStatus | 'all'>('all')
  const [productFilter, setProductFilter] = useState<StockDeliveryProduct | 'all'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 10

  const {
    items: items,
    total,
    isLoading,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<
    StockDeliveryItem,
    {
      stationId?: string
      status?: 'LOGGED' | 'VERIFIED'
      product?: StockDeliveryProduct
    }
  >(
    'stockDelivery',
    stockDeliveryBXService.list,
    {
      stationId: deferredStationId || undefined,
      status:
        statusFilter === 'all'
          ? undefined
          : statusFilter === 'Logged'
            ? 'LOGGED'
            : 'VERIFIED',
      product: productFilter === 'all' ? undefined : productFilter,
    },
    page,
    limit,
  )
  const exportCsv = () => {
    if (!items.length) return
    const csv = Papa.unparse(
      items.map((item) => ({
        Station: item.station,
        Product: item.product,
        'Depot Invoice Qty': formatStationLitres(item.depotInvoiceQty),
        'Quantity Received': formatStationLitres(item.quantityReceived),
        'Truck Siding': formatStationLitres(item.truckSiding),
        Date: item.date,
        Status: item.status,
      })),
    )
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `stock_delivery_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const renderStatusBadge = (status: StockDeliveryStatus) => {
    switch (status) {
      case 'Logged':
        return <span className='bx-sd-pill bx-sd-pill--overage'>Logged</span>
      case 'Verified':
        return <span className='bx-sd-pill bx-sd-pill--shortage'>Verified</span>
      default:
        return <span className='bx-sd-pill'>{status}</span>
    }
  }

  const totalPages = Math.ceil(total / limit) || 1

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    if (page <= 3) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    if (page >= totalPages - 2) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, '...', page - 1, page, page + 1, '...', totalPages]
  }

  return (
    <div className='bx-stock-delivery-page'>
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma & Reconciliation */}
      <div className='bx-stock-delivery-page__header'>
        <h2 className='bx-stock-delivery-page__title'>Stock Delivery</h2>
        <p className='bx-stock-delivery-page__subtitle'>
          Fuel deliveries logged by station managers, kept separate from the daily reporting flow
          since they don&apos;t happen every day.
        </p>
      </div>

      {/* Search & Actions Toolbar matching Reconciliation */}
      <div className='bx-stock-delivery-page__controls'>
        <div className='bx-stock-delivery-page__search'>
          <svg
            className='bx-stock-delivery-page__search-icon'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            type='search'
            aria-label='Filter stock deliveries by station ID'
            placeholder='Filter by station ID'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className='bx-stock-delivery-page__actions'>
          <div className='bx-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${
                statusFilter !== 'all' || productFilter !== 'all' ? 'active' : ''
              }`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-filter-menu'>
                <div className='bx-filter-menu__title'>Status</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('all')
                    setPage(1)
                  }}
                >
                  All Statuses
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'Logged' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Logged')
                    setPage(1)
                  }}
                >
                  Logged
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${statusFilter === 'Verified' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Verified')
                    setPage(1)
                  }}
                >
                  Verified
                </button>

                <div className='bx-filter-menu__divider' />

                <div className='bx-filter-menu__title'>Product</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                  }}
                >
                  AGO
                </button>

                <div className='bx-filter-menu__divider' />
                <button
                  type='button'
                  className='bx-filter-menu__item bx-filter-menu__item--done'
                  onClick={() => setIsFilterOpen(false)}
                >
                  Done
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isLoading || !items.length}
          >
            <img src={exportIcon} alt='' />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Main Table matching Figma and Reconciliation */}
      <div className='bx-stock-delivery-page__table-wrapper'>
        <table className='bx-stock-delivery-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Depot invoice qty</th>
              <th>Quantity received</th>
              <th>Truck siding</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className='bx-sd-empty'>
                  Loading stock deliveries...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className='bx-sd-empty'>
                  No stock deliveries found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-sd-col-station'>{item.station}</td>
                  <td className='bx-sd-col-product'>{item.product}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.depotInvoiceQty)}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.quantityReceived)}</td>
                  <td className='bx-sd-col-qty'>{formatStationLitres(item.truckSiding)}</td>
                  <td className='bx-sd-col-date'>{item.date}</td>
                  <td>{renderStatusBadge(item.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination matching Figma exactly */}
      <div className='bx-stock-delivery-page__pagination'>
        <div className='bx-stock-delivery-page__pagination-controls'>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Previous
          </button>

          <div className='bx-stock-delivery-page__pagination-numbers'>
            {getPaginationItems().map((item, idx) =>
              item === '...' ? (
                <span key={`dots-${idx}`} className='bx-pagination-dots'>
                  ...
                </span>
              ) : (
                <button
                  key={`page-${item}`}
                  type='button'
                  className={`bx-pagination-num ${page === item ? 'active' : ''}`}
                  onClick={() => setPage(Number(item))}
                >
                  {item}
                </button>
              ),
            )}
          </div>

          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default StockDelivery
